// Ratings — the four per-ride metrics, each against the fleet average.
//
// Was a collapsed accordion with a green `Chip` for the total and, under it, four rows
// printing "The average driver has a 4.55 score" as prose beside each number. The
// comparison IS the content — a bare 4.31 out of 5 says nothing on its own — so it is
// laid out as a comparison: `metric · fleet N · ±delta · N.NN / 5`, coloured only where
// the driver is below fleet.
//
// The header total is `totalScore` from the API, not an average of the four rows: the two
// are not the same number and the live card showed the former. It is also on a DIFFERENT
// SCALE — 0–100, where the rows are 0–5 — because it is a weighted composite that folds in
// nps and the cancellation score alongside these four. Hence `outOf={100}` on the header
// and nothing on the rows.
import { useCallback, useEffect } from 'react';
import { Skeleton } from '../../../ui/core';
import { StarIcon } from '../../../ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { getDriverScore, getAverageDriverScores } from '../../../redux/Users/actions';
import { SectionCard, EmptyRows } from '../../../pages/DriverProfile/components';
import {
  ScoreRow,
  TotalScore,
  TrailingMeta,
  SectionSkeleton,
  SectionError,
  useSectionFetch,
} from '../../../pages/UserProfile/parts';

/** API field → row label, in the order the card has always listed them. */
const METRICS = [
  ['conversationScore', 'Conversation'],
  ['cleanlinessScore', 'Cleanliness'],
  ['drivingQualityScore', 'Driving'],
  ['carQualityScore', 'Car quality'],
];

const num = (value) => (Number.isFinite(Number(value)) ? Number(value) : undefined);

const DriverScoreCard = ({ user }) => {
  const dispatch = useDispatch();
  const averages = useSelector((state) => state.users.averageDriverScores);
  const data = useSelector((state) => state.users.selection.driverScore);
  // Depended on as a boolean, not as the object: the fleet document is shared across
  // users and fetched once, and putting `averages` itself in the dep array would refetch
  // the driver's own scores the moment it arrived.
  const hasAverages = Boolean(averages);

  const loadScore = useCallback(() => getDriverScore(user.id)(dispatch), [user.id, dispatch]);
  const [status, retry] = useSectionFetch(loadScore);

  // The fleet document is not gated on: it is shared across users and only feeds the
  // comparison, so a driver's own scores still read without it.
  useEffect(() => {
    if (!hasAverages) {
      getAverageDriverScores()(dispatch);
    }
  }, [hasAverages, dispatch]);

  return (
    <SectionCard
      title="Ratings"
      icon={<StarIcon sx={{ fontSize: 17, color: 'text.secondary', flexShrink: 0 }} />}
      meta={
        <TrailingMeta>
          {status === 'loading' && <Skeleton variant="text" width={72} />}
          {/* 100, not 5: `totalScore` is the weighted composite, on a different scale
              from the four rows it heads. See TotalScore. */}
          {status === 'done' && data && (
            <TotalScore value={num(data.totalScore)} fleet={num(averages?.totalScore)} outOf={100} />
          )}
        </TrailingMeta>
      }
    >
      {status === 'loading' && <SectionSkeleton rows={4} />}
      {status === 'failed' && (
        <SectionError onRetry={retry}>Couldn&rsquo;t load this driver&rsquo;s ratings.</SectionError>
      )}
      {/* Fetched, and there is no `average_ratings` document: this driver has not been
          rated yet. Said plainly, because the card used to show four skeleton bars here
          and read as a page still loading. */}
      {status === 'done' && !data && <EmptyRows>Not rated yet.</EmptyRows>}
      {status === 'done' &&
        data &&
        METRICS.map(([field, label], i) => (
          <ScoreRow key={field} label={label} value={num(data[field])} fleet={num(averages?.[field])} first={i === 0} />
        ))}
    </SectionCard>
  );
};

export default DriverScoreCard;
